import { useMemo } from 'react';
import { useCompletions, useStreak, useTotalXp, type CompletionRow } from '../progress/hooks.js';
import { useHasProAccess } from '../subscription/hooks.js';
import { extractVocab } from '../vocab/extractVocab.js';
import { getCourseTree, isMainCourseLessonId } from '../content/course.js';
import { deriveTimeStats, type ProgressSnapshot } from './definitions.js';

/**
 * One hook that assembles the full ProgressSnapshot from the existing
 * progress hooks. Shared by the Awards screen and the Profile dashboard
 * (awards-unlocked tile) so the derivation logic lives once.
 */
export function useProgressSnapshot(): ProgressSnapshot & { lessonsCompleted: number } {
  const { data: completions = {} } = useCompletions();
  const { data: streak } = useStreak();
  const totalXp = useTotalXp();
  const hasProAccess = useHasProAccess();

  // "Words learned" is the main course's vocabulary: phrase-course lessons
  // (ფრაზები) teach multi-word expressions, not words (PHRASES.md § 3).
  const vocabCount = useMemo(
    () => extractVocab(Object.keys(completions).filter(isMainCourseLessonId)).length,
    [completions],
  );
  const treeCounts = useMemo(() => countCompletedTreeNodes(completions), [completions]);
  const time = useMemo(() => deriveTimeStats(completions), [completions]);

  return {
    completions,
    streak,
    totalXp,
    vocabCount,
    hasProAccess,
    skillsCompleted: treeCounts.skills,
    unitsCompleted: treeCounts.units,
    time,
    lessonsCompleted: Object.keys(completions).length,
  };
}

/** Skills / units where every lesson id appears in the completions map. */
export function countCompletedTreeNodes(completions: Record<string, CompletionRow>): {
  skills: number;
  units: number;
} {
  const tree = getCourseTree();
  if (!tree) return { skills: 0, units: 0 };
  let skills = 0;
  let units = 0;
  for (const unit of tree.units) {
    let unitDone = true;
    for (const skill of unit.resolvedSkills) {
      const skillDone =
        skill.lessons.length > 0 && skill.lessons.every((id) => id in completions);
      if (skillDone) skills += 1;
      else unitDone = false;
    }
    if (unitDone && unit.resolvedSkills.length > 0) units += 1;
  }
  return { skills, units };
}
